import { LoadingSpinner } from "components/LoadingSpinner";
import { parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import { Calendar, CalendarValueType } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Paginator, PaginatorPageState } from "primereact/paginator";
// FUTURE payer vs payer: import { SelectButton } from "primereact/selectbutton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// FUTURE payer vs payer: re-add IContractModelingRow + usePostContractModelingAnalysisMutation
import {
  ITermComparisonRow,
  useGetContractModelingAvailableCptCodesQuery,
  useGetContractModelingAvailableFeeTermDatesQuery,
  useGetContractModelingPayersWithDataQuery,
  usePostContractModelingTermComparisonMutation,
} from "store/queries/contractModeling";
import { useGetAllPayersQuery } from "store/queries/payers";
import styled from "styled-components";

import { exportToCSV } from "../MarketPriceAnalysis/helpers";

const StyledContainer = styled.div`
  border-radius: 8px;
  border: 1px solid var(--gray-new-200);
  display: flex;
  flex-direction: column;
  width: 100%;

  .p-multiselect .p-multiselect-label {
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .p-datatable {
    .bg-primary-100 {
      background-color: #f0f6ff !important;

      td {
        font-weight: 600;
        border-top: 2px solid var(--primary-color);
      }
    }
  }
`;

type ComparisonMode = "payer" | "term";

/** UI + requests use this only. Change to `"payer"` and uncomment FUTURE payer blocks to restore payer vs payer. */
const CONTRACT_MODELING_ACTIVE_MODE: ComparisonMode = "term";

/* FUTURE payer vs payer — restore with SelectButton + setMode:
const MODE_OPTIONS: { label: string; value: ComparisonMode }[] = [
  { label: "Payer comparison", value: "payer" },
  { label: "Term comparison", value: "term" },
];
*/
const MAX_INDIVIDUAL_COLUMNS = 3;

function formatIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDollars(value: number) {
  return parsePrice(value);
}

function formatPotentialPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(2)}%`;
}

function getMutationErrorMessage(err: unknown): string {
  if (!err || typeof err !== "object") return "Analysis request failed.";
  const anyErr = err as {
    data?: { message?: string };
    message?: string;
  };
  return anyErr.data?.message || anyErr.message || "Analysis request failed.";
}

interface ITableComparisonCell {
  key: string;
  label: string;
  fee: number;
  revenue: number;
}

interface ITableRow {
  cptCode: string;
  volume: number;
  primaryFee: number;
  primaryRevenue: number;
  comparisons: ITableComparisonCell[];
  revenuePotential: number;
  revenuePotentialPercent: number | null;
  isTotalsRow?: boolean;
  comparisonAvgFee?: number;
  comparisonAvgRevenue?: number;
  totalsRevenuePotentialPercent?: number | null;
}

export function ContractModelingAnalysis({
  specialty,
}: {
  /** Same values as Payer AI Insights / contract-management (e.g. FACILITY, PROVIDER). */
  specialty: string;
}) {
  const mode = CONTRACT_MODELING_ACTIVE_MODE;

  const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>([]);
  const [first, setFirst] = useState(0);
  const [rows] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);

  /* FUTURE payer vs payer — state (restore with useState + MODE_OPTIONS / setMode):
  const [primaryPayerId, setPrimaryPayerId] = useState<number | null>(null);
  const [comparisonPayerIds, setComparisonPayerIds] = useState<number[]>([]);
  const [serviceDateRange, setServiceDateRange] = useState<Date[]>([]);
  const [feeEffectiveDate, setFeeEffectiveDate] = useState<string | null>(null);
  */

  // Term comparison (effective dates as ISO strings)
  const [termPayerId, setTermPayerId] = useState<number | null>(null);
  const [primaryEffectiveDate, setPrimaryEffectiveDate] = useState<
    string | null
  >(null);
  const [comparisonEffectiveDates, setComparisonEffectiveDates] = useState<
    string[]
  >([]);
  const [termClaimDateRange, setTermClaimDateRange] = useState<Date[]>([]);

  const { data: payersData, isLoading: isLoadingPayersCatalog } =
    useGetAllPayersQuery();
  const {
    data: payersWithModelingData = [],
    isLoading: isLoadingPayersWithData,
  } = useGetContractModelingPayersWithDataQuery(
    { specialty },
    { skip: !specialty }
  );

  /* FUTURE payer vs payer — claim window for POST /analysis:
  const startDatePayer = useMemo(
    () => formatIsoDate(serviceDateRange[0] ?? null),
    [serviceDateRange]
  );
  const endDatePayer = useMemo(
    () => formatIsoDate(serviceDateRange[1] ?? null),
    [serviceDateRange]
  );
  */
  const termStartDate = useMemo(
    () => formatIsoDate(termClaimDateRange[0] ?? null),
    [termClaimDateRange]
  );
  const termEndDate = useMemo(
    () => formatIsoDate(termClaimDateRange[1] ?? null),
    [termClaimDateRange]
  );

  /* FUTURE payer vs payer — CPT + fee term dates + POST /contract-modeling/analysis:
  const { data: cptOptionsPayer = [], isLoading: isLoadingCptsPayer } =
    useGetContractModelingAvailableCptCodesQuery(
      mode === "payer"
        ? {
            payerId: primaryPayerId ?? undefined,
            startDate: startDatePayer,
            endDate: endDatePayer,
          }
        : undefined,
      { skip: mode !== "payer" }
    );

  const { data: feeTermDatesPayer = [], isLoading: isLoadingTermsPayer } =
    useGetContractModelingAvailableFeeTermDatesQuery(primaryPayerId ?? 0, {
      skip: mode !== "payer" || !primaryPayerId,
    });

  const [
    postPayerAnalysis,
    {
      data: payerData,
      isLoading: isLoadingPayerAnalysis,
      error: payerError,
      reset: resetPayerFn,
    },
  ] = usePostContractModelingAnalysisMutation();

  const resetPayerRef = useRef(resetPayerFn);
  resetPayerRef.current = resetPayerFn;
  const resetPayer = useCallback(() => resetPayerRef.current(), []);
  */

  const { data: cptOptionsTerm = [], isLoading: isLoadingCptsTerm } =
    useGetContractModelingAvailableCptCodesQuery(
      termPayerId ? { payerId: termPayerId, specialty } : undefined,
      { skip: !termPayerId }
    );

  const { data: feeTermDatesTerm = [], isLoading: isLoadingTermsTerm } =
    useGetContractModelingAvailableFeeTermDatesQuery(
      { payerId: termPayerId ?? 0, specialty },
      { skip: !termPayerId }
    );

  const [
    postTermComparison,
    {
      data: termData,
      isLoading: isLoadingTermAnalysis,
      error: termError,
      reset: resetTermFn,
    },
  ] = usePostContractModelingTermComparisonMutation();

  const resetTermRef = useRef(resetTermFn);
  resetTermRef.current = resetTermFn;
  const resetTerm = useCallback(() => resetTermRef.current(), []);

  const payerOptions = useMemo(() => {
    if (!payersWithModelingData.length) return [];
    const catalogById = new Map<number, { name: string; archivedAt?: string }>(
      (payersData?.data ?? []).map(
        (payer: { id: number; name: string; archivedAt?: string }) => [
          payer.id,
          { name: payer.name, archivedAt: payer.archivedAt },
        ]
      )
    );
    return payersWithModelingData
      .map((ref) => {
        const catalog = catalogById.get(ref.id);
        const label = (
          ref.name?.trim() ||
          catalog?.name ||
          `Payer ${ref.id}`
        ).trim();
        if (catalog?.archivedAt) return null;
        if (label === "Clear Health") return null;
        return { label, value: ref.id };
      })
      .filter((o): o is { label: string; value: number } => o !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [payersWithModelingData, payersData]);

  /* FUTURE payer vs payer — comparison payer dropdown + fee term options for primary payer:
  const comparisonPayerOptions = useMemo(
    () =>
      payerOptions.filter(
        (o: { label: string; value: number }) => o.value !== primaryPayerId
      ),
    [payerOptions, primaryPayerId]
  );

  const feeTermDateOptionsPayer = useMemo(
    () =>
      feeTermDatesPayer.map((d) => ({
        label: d,
        value: d,
      })),
    [feeTermDatesPayer]
  );
  */

  const feeTermDateOptionsTerm = useMemo(
    () =>
      feeTermDatesTerm.map((d) => ({
        label: d,
        value: d,
      })),
    [feeTermDatesTerm]
  );

  const comparisonTermDateOptions = useMemo(
    () =>
      feeTermDateOptionsTerm.filter((o) => o.value !== primaryEffectiveDate),
    [feeTermDateOptionsTerm, primaryEffectiveDate]
  );

  const primaryDisplayName = useMemo(() => {
    if (termData?.payer?.name) {
      return termData.payer.name;
    }
    if (termPayerId != null) {
      return (
        payerOptions.find((o) => o.value === termPayerId)?.label ?? "Payer"
      );
    }
    return "Payer";
  }, [termData, termPayerId, payerOptions]);

  /* FUTURE payer vs payer — primary column label when mode === "payer":
  const primaryDisplayNameWhenPayer = useMemo(() => {
    if (mode === "payer" && payerData?.primaryPayer?.name) {
      return payerData.primaryPayer.name;
    }
    if (mode === "payer" && primaryPayerId != null) {
      return (
        payerOptions.find((o) => o.value === primaryPayerId)?.label ?? "Primary"
      );
    }
    return "Primary";
  }, [mode, payerData, primaryPayerId, payerOptions]);
  */

  /* FUTURE payer vs payer:
  const distinctComparisonIds = useMemo(() => {
    if (primaryPayerId == null) return [];
    return [...new Set(comparisonPayerIds)].filter(
      (id) => id !== primaryPayerId
    );
  }, [comparisonPayerIds, primaryPayerId]);
  */

  const distinctComparisonTermDates = useMemo(() => {
    if (!primaryEffectiveDate) return [];
    return [...new Set(comparisonEffectiveDates)].filter(
      (d) => d !== primaryEffectiveDate
    );
  }, [comparisonEffectiveDates, primaryEffectiveDate]);

  /* FUTURE payer vs payer:
  const isPayerAggregated =
    distinctComparisonIds.length > MAX_INDIVIDUAL_COLUMNS;
  */
  const isTermAggregated =
    distinctComparisonTermDates.length > MAX_INDIVIDUAL_COLUMNS;

  /* FUTURE payer vs payer — effects for primary / comparison payers + analysis POST:
  useEffect(() => {
    if (mode !== "payer") return;
    setComparisonPayerIds((prev) => {
      const filtered = prev.filter((id) => id !== primaryPayerId);
      return filtered.length === prev.length ? prev : filtered;
    });
    setFeeEffectiveDate(null);
    setFirst(0);
    resetPayer();
  }, [primaryPayerId, resetPayer, mode]);

  useEffect(() => {
    if (mode !== "payer") return;
    if (
      feeTermDatesPayer.length > 0 &&
      !feeEffectiveDate &&
      !isLoadingTermsPayer
    ) {
      setFeeEffectiveDate(feeTermDatesPayer[0]);
    }
  }, [feeTermDatesPayer, feeEffectiveDate, isLoadingTermsPayer, mode]);

  const payerCompKey = comparisonPayerIds.join(",");
  */

  const cptKey = selectedCptCodes.join(",");

  useEffect(() => {
    setFirst(0);
  }, [cptKey, comparisonEffectiveDates, termStartDate, termEndDate]);

  /* FUTURE payer vs payer:
  useEffect(() => {
    if (mode !== "payer") return;
    if (distinctComparisonIds.length === 0) resetPayer();
  }, [distinctComparisonIds.length, resetPayer, mode]);

  useEffect(() => {
    if (mode !== "payer") return;
    if (
      !primaryPayerId ||
      distinctComparisonIds.length === 0 ||
      !feeEffectiveDate
    )
      return;
    postPayerAnalysis({
      primaryPayerId,
      comparisonPayerIds,
      feeEffectiveDate,
      cptCodes: selectedCptCodes.length > 0 ? selectedCptCodes : undefined,
      startDate: startDatePayer,
      endDate: endDatePayer,
    });
  }, [
    mode,
    primaryPayerId,
    payerCompKey,
    cptKey,
    startDatePayer,
    endDatePayer,
    feeEffectiveDate,
  ]);
  */

  useEffect(() => {
    if (mode !== "term") return;
    setTermPayerId(null);
    setPrimaryEffectiveDate(null);
    setComparisonEffectiveDates([]);
    setSelectedCptCodes([]);
    setTermClaimDateRange([]);
    setFirst(0);
    resetTerm();
  }, [specialty, resetTerm, mode]);

  useEffect(() => {
    if (mode !== "term") return;
    setPrimaryEffectiveDate(null);
    setComparisonEffectiveDates([]);
    setSelectedCptCodes([]);
    setTermClaimDateRange([]);
    setFirst(0);
    resetTerm();
  }, [termPayerId, resetTerm, mode]);

  useEffect(() => {
    if (mode !== "term") return;
    setComparisonEffectiveDates((prev) => {
      const filtered = prev.filter((d) => d !== primaryEffectiveDate);
      return filtered.length === prev.length ? prev : filtered;
    });
    setFirst(0);
    resetTerm();
  }, [primaryEffectiveDate, resetTerm, mode]);

  useEffect(() => {
    if (mode !== "term") return;
    if (
      feeTermDatesTerm.length > 0 &&
      !primaryEffectiveDate &&
      !isLoadingTermsTerm
    ) {
      setPrimaryEffectiveDate(feeTermDatesTerm[0]);
    }
  }, [feeTermDatesTerm, primaryEffectiveDate, isLoadingTermsTerm, mode]);

  const termCompKey = comparisonEffectiveDates.join(",");

  useEffect(() => {
    if (mode !== "term") return;
    if (distinctComparisonTermDates.length === 0) resetTerm();
  }, [distinctComparisonTermDates.length, resetTerm, mode]);

  useEffect(() => {
    if (mode !== "term") return;
    if (
      !termPayerId ||
      !primaryEffectiveDate ||
      distinctComparisonTermDates.length === 0
    )
      return;
    postTermComparison({
      payerId: termPayerId,
      primaryEffectiveDate,
      comparisonEffectiveDates,
      specialty,
      cptCodes: selectedCptCodes.length > 0 ? selectedCptCodes : undefined,
      startDate: termStartDate,
      endDate: termEndDate,
    });
  }, [
    mode,
    specialty,
    termPayerId,
    primaryEffectiveDate,
    termCompKey,
    cptKey,
    termStartDate,
    termEndDate,
  ]);

  /* FUTURE payer vs payer — reset filters when switching tabs (restore with [mode] deps):
  useEffect(() => {
    setFirst(0);
    setSelectedCptCodes([]);
    setIsDownloading(false);
  }, [mode]);
  */

  const activeIsLoading = isLoadingTermAnalysis;
  const activeError = termError;
  const activeHasComparisons = distinctComparisonTermDates.length > 0;
  const activePayerSelected = termPayerId != null;
  const activeReadyForRequest = Boolean(primaryEffectiveDate);
  const isAggregated = isTermAggregated;

  const rawRows = termData?.rows;

  const comparisonColumnHeaders = useMemo((): {
    key: string;
    label: string;
  }[] => {
    if (!rawRows?.length) return [];
    /* FUTURE payer vs payer:
    if (mode === "payer") {
      return (
        (rawRows as IContractModelingRow[])[0]?.comparisonPayers.map((c) => ({
          key: String(c.payerId),
          label: c.payerName,
        })) ?? []
      );
    }
    */
    return (
      (rawRows as ITermComparisonRow[])[0]?.comparisonTerms.map((c) => ({
        key: c.effectiveDate,
        label: c.effectiveDate,
      })) ?? []
    );
  }, [rawRows]);

  const tableData = useMemo((): ITableRow[] => {
    if (!rawRows?.length) return [];

    const toTableRow = (row: ITermComparisonRow): ITableRow => {
      /* FUTURE payer vs payer — branch when mode === "payer":
      const toTableRowPayer = (row: IContractModelingRow): ITableRow => ({
        cptCode: row.cptCode,
        volume: row.volume,
        primaryFee: row.primaryPayer.feeAmount,
        primaryRevenue: row.primaryPayer.revenue,
        comparisons: row.comparisonPayers.map((c) => ({
          key: String(c.payerId),
          label: c.payerName,
          fee: c.feeAmount,
          revenue: c.revenue,
        })),
        revenuePotential: row.revenuePotential,
        revenuePotentialPercent: row.revenuePotentialPercent,
      });
      */
      const r = row;
      return {
        cptCode: r.cptCode,
        volume: r.volume,
        primaryFee: r.primaryTerm.feeAmount,
        primaryRevenue: r.primaryTerm.revenue,
        comparisons: r.comparisonTerms.map((c) => ({
          key: c.effectiveDate,
          label: c.effectiveDate,
          fee: c.feeAmount,
          revenue: c.revenue,
        })),
        revenuePotential: r.revenuePotential,
        revenuePotentialPercent: r.revenuePotentialPercent,
      };
    };

    const paginated = (rawRows as ITermComparisonRow[])
      .slice(first, first + rows)
      .map(toTableRow);

    let totalPrimary = 0;
    const totalCompMap = new Map<string, { fee: number; revenue: number }>();
    let totalRevPotential = 0;
    let totalsRpPct: number | null = null;

    /* FUTURE payer vs payer — totals from payerData:
    if (mode === "payer" && payerData) {
      totalPrimary = payerData.totals.primaryPayerRevenue;
      payerData.totals.comparisonPayerRevenues.forEach((t) => {
        totalCompMap.set(String(t.payerId), {
          fee: 0,
          revenue: t.totalRevenue,
        });
      });
      totalRevPotential = payerData.totals.revenuePotential.difference;
      totalsRpPct =
        payerData.totals.revenuePotential.revenuePotentialPercent ?? null;
    } else */
    if (termData) {
      totalPrimary = termData.totals.primaryRevenue;
      termData.totals.comparisonRevenues.forEach((t) => {
        totalCompMap.set(t.effectiveDate, {
          fee: 0,
          revenue: t.totalRevenue,
        });
      });
      totalRevPotential = termData.totals.revenuePotential.difference;
      totalsRpPct =
        termData.totals.revenuePotential.revenuePotentialPercent ?? null;
    }

    const totalComparisons = comparisonColumnHeaders.map((h) => ({
      key: h.key,
      label: h.label,
      fee: totalCompMap.get(h.key)?.fee ?? 0,
      revenue: totalCompMap.get(h.key)?.revenue ?? 0,
    }));

    const compCount = totalComparisons.length || 1;
    const avgRev =
      totalComparisons.reduce((s, c) => s + c.revenue, 0) / compCount;
    const avgFee = totalComparisons.reduce((s, c) => s + c.fee, 0) / compCount;

    const totalsRow: ITableRow = {
      cptCode: "Totals",
      volume: 0,
      primaryFee: 0,
      primaryRevenue: totalPrimary,
      comparisons: totalComparisons,
      revenuePotential: totalRevPotential,
      revenuePotentialPercent: totalsRpPct,
      isTotalsRow: true,
      comparisonAvgFee: avgFee,
      comparisonAvgRevenue: avgRev,
      totalsRevenuePotentialPercent: totalsRpPct,
    };

    return [...paginated, totalsRow];
  }, [rawRows, first, rows, termData, comparisonColumnHeaders]);

  const onPageChange = (event: PaginatorPageState) => setFirst(event.first);

  const handleDownloadData = async () => {
    if (!rawRows?.length) return;
    try {
      setIsDownloading(true);
      const allRows = (rawRows as ITermComparisonRow[]).map((row) => {
        /* FUTURE payer vs payer — restore full `if (mode === "payer") { ... IContractModelingRow ... }` branch here for CSV export. */
        const r = row;
        const base: Record<string, string | number> = {
          CPT: r.cptCode,
          Volume: r.volume,
          [`${primaryDisplayName} (unit fee)`]: formatDollars(
            r.primaryTerm.feeAmount
          ),
          [primaryDisplayName]: formatDollars(r.primaryTerm.revenue),
        };
        if (isAggregated) {
          const n = r.comparisonTerms.length || 1;
          const avgF =
            r.comparisonTerms.reduce((s, c) => s + c.feeAmount, 0) / n;
          const avgR = r.comparisonTerms.reduce((s, c) => s + c.revenue, 0) / n;
          base["Comparison avg (unit fee)"] = formatDollars(avgF);
          base["Comparison avg revenue"] = formatDollars(avgR);
        } else {
          r.comparisonTerms.forEach((c) => {
            base[`${c.effectiveDate} (unit fee)`] = formatDollars(c.feeAmount);
            base[c.effectiveDate] = formatDollars(c.revenue);
          });
        }
        base["Revenue potential"] = formatDollars(r.revenuePotential);
        base["Revenue potential %"] = formatPotentialPercent(
          r.revenuePotentialPercent
        );
        return base;
      });
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const prefix = "contract_modeling_term";
      /* FUTURE payer vs payer: const prefix = mode === "payer" ? "contract_modeling_payer" : "contract_modeling_term"; */
      exportToCSV(allRows, `${prefix}_${timestamp}.csv`);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoadingPayersCatalog || isLoadingPayersWithData) {
    return <LoadingSpinner />;
  }

  if (!payersWithModelingData.length) {
    return (
      <StyledContainer className="bg-white">
        <div className="p-4">
          <div className="flex gap-2 align-items-center mb-3">
            <i className="pi pi-table text-2xl text-primary" />
            <p className="text-lg font-bold">Contract modeling</p>
          </div>
          <div className="text-center p-4 text-gray-500">
            No payers with claim data were found for this account, so contract
            modeling is not available yet.
          </div>
        </div>
      </StyledContainer>
    );
  }

  const cptOpts = cptOptionsTerm;
  const isCptLoading = isLoadingCptsTerm;
  const totalRows = rawRows?.length ?? 0;

  /* FUTURE payer vs payer — fee effective date dropdown placeholder:
  let payerFeeDropdownPlaceholder = "Select contract term date";
  if (isLoadingTermsPayer) {
    payerFeeDropdownPlaceholder = "Loading…";
  } else if (!primaryPayerId) {
    payerFeeDropdownPlaceholder = "Select primary payer first";
  }
  */

  let termPrimaryEffectivePlaceholder = "Select effective date";
  if (isLoadingTermsTerm) {
    termPrimaryEffectivePlaceholder = "Loading…";
  } else if (!termPayerId) {
    termPrimaryEffectivePlaceholder = "Select payer first";
  }

  return (
    <StyledContainer className="bg-white">
      <div className="p-4">
        <div className="flex justify-content-between align-items-center mb-3">
          <div className="flex gap-2 align-items-center">
            <i className="pi pi-table text-2xl text-primary" />
            <p className="text-lg font-bold">Contract modeling</p>
          </div>
          {rawRows && !activeIsLoading && rawRows.length > 0 && (
            <Button
              label="Download"
              icon="pi pi-download"
              onClick={handleDownloadData}
              loading={isDownloading}
              className="p-button-outlined max-h-3rem max-w-12rem"
            />
          )}
        </div>

        <p className="text-sm text-gray-600 mb-3">
          Volume reflects claim lines for the payer(s) in the selected service
          date window.
        </p>

        {/* FUTURE payer vs payer — tab switch + payer form (restore MODE_OPTIONS, setMode, primary/comparison payers, fee date, claim range):
        <div className="mb-3">
          <SelectButton
            value={mode}
            options={MODE_OPTIONS}
            onChange={(e) => {
              if (e.value) setMode(e.value as ComparisonMode);
            }}
          />
        </div>

        {mode === "payer" && (
          <div className="flex flex-wrap gap-3 mb-4 align-items-end">
            <div
              className="flex flex-column gap-2"
              style={{ minWidth: "220px" }}
            >
              <label className="text-sm font-semibold">Primary payer</label>
              <Dropdown
                value={primaryPayerId}
                options={payerOptions}
                onChange={(e) => setPrimaryPayerId(e.value ?? null)}
                placeholder="Select primary payer"
                showClear
                filter
              />
            </div>

            <div
              className="flex flex-column gap-2 flex-grow-1"
              style={{ minWidth: "260px" }}
            >
              <label className="text-sm font-semibold">Comparison payers</label>
              <MultiSelect
                value={comparisonPayerIds}
                options={comparisonPayerOptions}
                onChange={(e) => setComparisonPayerIds(e.value ?? [])}
                placeholder={
                  !primaryPayerId
                    ? "Select a primary payer first"
                    : "Select one or more payers to compare"
                }
                filter
                showClear
                display="chip"
                className="w-full"
                disabled={!primaryPayerId}
              />
            </div>

            <div
              className="flex flex-column gap-2"
              style={{ minWidth: "220px" }}
            >
              <label className="text-sm font-semibold">
                Fee effective date
              </label>
              <Dropdown
                value={feeEffectiveDate}
                options={feeTermDateOptionsPayer}
                onChange={(e) => setFeeEffectiveDate(e.value ?? null)}
                placeholder={payerFeeDropdownPlaceholder}
                showClear
                filter
                disabled={!primaryPayerId || isLoadingTermsPayer}
              />
            </div>

            <div className="flex flex-wrap gap-3 align-items-end">
              <div
                className="flex flex-column gap-2"
                style={{
                  minWidth: "240px",
                  width: "240px",
                  maxWidth: "100%",
                }}
              >
                <label className="text-sm font-semibold">
                  Claim service date range
                </label>
                <Calendar
                  selectionMode="range"
                  placeholder="Optional"
                  value={serviceDateRange}
                  onChange={(e: { value: CalendarValueType }) => {
                    const dates = e.value as Date[];
                    setServiceDateRange(dates || []);
                  }}
                  showIcon
                  dateFormat="mm/dd/yy"
                  className="w-full"
                />
              </div>

              <div
                className="flex flex-column gap-2"
                style={{ width: "200px", maxWidth: "100%" }}
              >
                <label className="text-sm font-semibold">CPT (optional)</label>
                <MultiSelect
                  value={selectedCptCodes}
                  options={cptOpts.map((c) => ({ label: c, value: c }))}
                  onChange={(e) => setSelectedCptCodes(e.value ?? [])}
                  placeholder={isCptLoading ? "Loading…" : "All if empty"}
                  filter
                  showClear
                  display="comma"
                  maxSelectedLabels={2}
                  className="w-full"
                  disabled={isCptLoading}
                  emptyFilterMessage="No CPT codes match"
                />
              </div>
            </div>
          </div>
        )}
        */}

        <div className="flex flex-wrap gap-3 mb-4 align-items-end">
          <div className="flex flex-column gap-2" style={{ minWidth: "220px" }}>
            <label className="text-sm font-semibold">Payer</label>
            <Dropdown
              value={termPayerId}
              options={payerOptions}
              onChange={(e) => setTermPayerId(e.value ?? null)}
              placeholder="Select payer"
              showClear
              filter
            />
          </div>

          <div className="flex flex-column gap-2" style={{ minWidth: "220px" }}>
            <label className="text-sm font-semibold">
              Primary term (effective date)
            </label>
            <Dropdown
              value={primaryEffectiveDate}
              options={feeTermDateOptionsTerm}
              onChange={(e) => setPrimaryEffectiveDate(e.value ?? null)}
              placeholder={termPrimaryEffectivePlaceholder}
              showClear
              filter
              disabled={!termPayerId || isLoadingTermsTerm}
            />
          </div>

          <div className="flex flex-column gap-2" style={{ minWidth: "220px" }}>
            <label className="text-sm font-semibold">
              Comparison term dates
            </label>
            <MultiSelect
              value={comparisonEffectiveDates}
              options={comparisonTermDateOptions}
              onChange={(e) => setComparisonEffectiveDates(e.value ?? [])}
              placeholder={
                !primaryEffectiveDate
                  ? "Select primary term first"
                  : "Select one or more dates"
              }
              display="chip"
              className="w-full"
              filter
              disabled={!primaryEffectiveDate}
            />
          </div>

          <div
            className="flex flex-column gap-2"
            style={{
              minWidth: "240px",
              width: "240px",
              maxWidth: "100%",
            }}
          >
            <label className="text-sm font-semibold">
              Claim service date range (optional)
            </label>
            <Calendar
              selectionMode="range"
              placeholder="Optional"
              value={termClaimDateRange}
              onChange={(e: { value: CalendarValueType }) => {
                const dates = e.value as Date[];
                setTermClaimDateRange(dates || []);
              }}
              showIcon
              dateFormat="mm/dd/yy"
              className="w-full"
            />
          </div>

          <div
            className="flex flex-column gap-2"
            style={{ width: "200px", maxWidth: "100%" }}
          >
            <label className="text-sm font-semibold">CPT (optional)</label>
            <MultiSelect
              value={selectedCptCodes}
              options={cptOpts.map((c) => ({ label: c, value: c }))}
              onChange={(e) => setSelectedCptCodes(e.value ?? [])}
              placeholder={isCptLoading ? "Loading…" : "All if empty"}
              filter
              showClear
              display="comma"
              maxSelectedLabels={2}
              className="w-full"
              disabled={isCptLoading}
              emptyFilterMessage="No CPT codes match"
            />
          </div>
        </div>

        {activeError && (
          <div className="p-3 mb-3 border-round border-1 border-red-200 bg-red-50 text-red-900 text-sm">
            {getMutationErrorMessage(activeError)}
          </div>
        )}

        {activeIsLoading && (
          <div className="flex justify-content-center p-5">
            <LoadingSpinner />
          </div>
        )}

        {activePayerSelected &&
          activeReadyForRequest &&
          activeHasComparisons &&
          rawRows &&
          !activeIsLoading && (
            <div className="w-full">
              {rawRows.length > 0 ? (
                <>
                  <DataTable
                    value={tableData}
                    stripedRows
                    showGridlines
                    responsiveLayout="scroll"
                    className="mb-4"
                    rowClassName={(rowData) => ({
                      "bg-primary-100 font-bold": rowData.isTotalsRow,
                    })}
                  >
                    <Column
                      field="cptCode"
                      header="CPT Code"
                      style={{ width: "100px" }}
                    />
                    <Column
                      field="volume"
                      header="Volume"
                      body={(row: ITableRow) =>
                        row.isTotalsRow ? "-" : row.volume
                      }
                      style={{ width: "80px" }}
                    />
                    <Column
                      header={`${primaryDisplayName} (unit fee)`}
                      body={(row: ITableRow) =>
                        row.isTotalsRow ? "—" : formatDollars(row.primaryFee)
                      }
                      style={{ minWidth: "200px", width: "200px" }}
                    />
                    <Column
                      header={primaryDisplayName}
                      body={(row: ITableRow) =>
                        formatDollars(row.primaryRevenue)
                      }
                      style={{ width: "130px" }}
                    />
                    {isAggregated ? (
                      <>
                        <Column
                          header="Comparison avg (unit fee)"
                          body={(row: ITableRow) => {
                            if (row.isTotalsRow) return "—";
                            const n = row.comparisons.length || 1;
                            const avg =
                              row.comparisons.reduce((s, c) => s + c.fee, 0) /
                              n;
                            return formatDollars(avg);
                          }}
                          style={{ minWidth: "200px", width: "200px" }}
                        />
                        <Column
                          header="Comparison avg revenue"
                          body={(row: ITableRow) => {
                            if (row.isTotalsRow) {
                              return formatDollars(
                                row.comparisonAvgRevenue ?? 0
                              );
                            }
                            const n = row.comparisons.length || 1;
                            const avg =
                              row.comparisons.reduce(
                                (s, c) => s + c.revenue,
                                0
                              ) / n;
                            return formatDollars(avg);
                          }}
                          style={{ width: "160px" }}
                        />
                      </>
                    ) : (
                      comparisonColumnHeaders.flatMap((col) => [
                        <Column
                          key={`${col.key}-fee`}
                          header={`${col.label} (unit fee)`}
                          body={(row: ITableRow) => {
                            if (row.isTotalsRow) return "—";
                            const cell = row.comparisons.find(
                              (c) => c.key === col.key
                            );
                            return cell ? formatDollars(cell.fee) : "—";
                          }}
                          style={{ minWidth: "200px", width: "200px" }}
                        />,
                        <Column
                          key={`${col.key}-rev`}
                          header={col.label}
                          body={(row: ITableRow) => {
                            const cell = row.comparisons.find(
                              (c) => c.key === col.key
                            );
                            return cell ? formatDollars(cell.revenue) : "—";
                          }}
                          style={{ width: "130px" }}
                        />,
                      ])
                    )}
                    <Column
                      header="Revenue potential"
                      body={(row: ITableRow) =>
                        formatDollars(row.revenuePotential)
                      }
                      style={{ width: "130px" }}
                    />
                    <Column
                      header="Revenue potential %"
                      body={(row: ITableRow) =>
                        formatPotentialPercent(
                          row.isTotalsRow
                            ? row.totalsRevenuePotentialPercent
                            : row.revenuePotentialPercent
                        )
                      }
                      style={{ minWidth: "160px", width: "160px" }}
                    />
                  </DataTable>
                  {totalRows > rows && (
                    <Paginator
                      first={first}
                      rows={rows}
                      totalRecords={totalRows}
                      onPageChange={onPageChange}
                    />
                  )}
                </>
              ) : (
                <div className="text-center p-4 text-gray-500">
                  No CPT rows with a resolvable contract fee for the primary
                  selection and filters.
                </div>
              )}
            </div>
          )}

        {!activePayerSelected && !activeIsLoading && (
          <div className="text-center p-4 text-gray-500">
            Select a payer to run term comparison.
            {/* FUTURE payer vs payer: "Select a primary payer to run contract modeling." */}
          </div>
        )}

        {activePayerSelected && !activeReadyForRequest && !activeIsLoading && (
          <div className="text-center p-4 text-gray-500">
            Select a primary term effective date to load comparison.
            {/* FUTURE payer vs payer: fee effective date prompt for payer mode */}
          </div>
        )}

        {activePayerSelected &&
          activeReadyForRequest &&
          !activeHasComparisons &&
          !activeIsLoading && (
            <div className="text-center p-4 text-gray-500">
              Select at least one comparison term date other than the primary
              term.
              {/* FUTURE payer vs payer: comparison payer prompt */}
            </div>
          )}
      </div>
    </StyledContainer>
  );
}
